import {
  ROUTE_MERGE_COMPANIES,
  ROUTE_PARAM_MERGE_COMPANIES_USER_ID_KEEP,
  ROUTE_PARAM_MERGE_COMPANIES_USER_ID_REMOVE,
} from "@/constants/routes.constants";
import { MergeCompaniesContext } from "@/services/MergeCompaniesContext/mergeCompaniesContext";
import { Button } from "@/shadcn/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shadcn/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn/components/ui/popover";
import { UserType } from "@/types";
import { updateQueryParam } from "@/utils/updateQueryParam";
import { ChevronsUpDown } from "lucide-react";
import React, { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router";

type MergeCompaniesUserSelectionProps = {
  side: "keep" | "remove";
};

const MergeCompaniesUserSelection: React.FC<
  MergeCompaniesUserSelectionProps
> = ({ side }) => {
  const {
    keepSideUser,
    removeSideUser,
    allUsers,
    setKeepSideCompany,
    setRemoveSideCompany,
  } = useContext(MergeCompaniesContext);
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  function handleUserSelection(userID: UserType["id"]) {
    if (side === "keep" && userID === keepSideUser?.id) {
      // If the user is already selected for keep side, do not change it
      console.warn("User is already selected for keep side.");
      return;
    }

    if (side === "remove" && userID === removeSideUser?.id) {
      // If the user is already selected for remove side, do not change it
      console.warn("User is already selected for remove side.");
      return;
    }

    // useLocation must be called at the component level, so location is captured above
    // This approach keeps both user IDs in the query string, preventing accidental overwrite
    const search = location.search;
    const paramKey =
      side === "keep"
        ? ROUTE_PARAM_MERGE_COMPANIES_USER_ID_KEEP
        : ROUTE_PARAM_MERGE_COMPANIES_USER_ID_REMOVE;
    const newSearch = updateQueryParam(search, paramKey, userID);
    // setKeepSideCompany(null);
    // setRemoveSideCompany(null);
    if (side === "keep") {
      setKeepSideCompany(null);
    } else {
      setRemoveSideCompany(null);
    }
    nav(`${ROUTE_MERGE_COMPANIES}?${newSearch}`);
  }

  // Filter users based on search input
  const filteredUsers = allUsers.filter((user) => {
    const searchLower = searchValue.toLowerCase();
    return (
      user.full_name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="m-auto flex w-full max-w-md flex-col justify-center">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={
              side === "keep"
                ? !keepSideUser
                  ? "outline"
                  : "secondary"
                : !removeSideUser
                  ? "outline"
                  : "secondary"
            }
            aria-haspopup="listbox"
            className="relative"
            size={"lg"}
          >
            <div>
              {side === "keep" ? (
                keepSideUser ? (
                  <div className="flex flex-col items-center text-center">
                    <span>{keepSideUser.full_name}</span>
                    <span className="text-xs text-gray-300">
                      {keepSideUser.email}
                    </span>
                  </div>
                ) : (
                  <div className="flex text-white">Select a user to merge</div>
                )
              ) : null}
              {side === "remove" ? (
                removeSideUser ? (
                  <div className="flex flex-col items-center text-center">
                    <span>{removeSideUser.full_name}</span>
                    <span className="text-xs text-gray-300">
                      {removeSideUser.email}
                    </span>
                  </div>
                ) : (
                  <div className="flex text-white">Select a user to remove</div>
                )
              ) : null}
            </div>
            <ChevronsUpDown className="pointer-events-none absolute top-1/2 right-2 h-4 w-4 -translate-y-1/2 text-gray-300" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[350px] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search users..."
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList>
              <CommandEmpty>No users found.</CommandEmpty>
              <CommandGroup>
                {filteredUsers
                  // sort by latest first using created_at
                  .sort((a, b) => {
                    return b.created_at.getTime() - a.created_at.getTime();
                  })
                  .map((user) => (
                    <CommandItem
                      key={user.id}
                      value={user.id}
                      onSelect={() => {
                        handleUserSelection(user.id);
                        setOpen(false);
                        setSearchValue(""); // Reset search when closing
                      }}
                      className="flex cursor-pointer flex-col items-start gap-0"
                    >
                      <div>{user.full_name}</div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default MergeCompaniesUserSelection;
